import { cloneDeep } from '@apollo/client/utilities';
import { CsvUploadFileType } from '../graphql/csvUploadConfiguration.graphql';
import { CompanyEntity, forDelete } from './common/entity';
import { CustomField } from './custom_field/customField';
import { t } from './translation/Translator';
import { TranslationKey } from './translation/TranslationKey';

export enum CsvUploadTemplate {
  product = 'product_template.csv',
  bin = 'bin_template.csv',
  productMasterData = 'pmd_template.csv',
  user = 'user_template.csv',
  stockLocation = 'stock_location_template.csv',
  lot = 'lot_template.csv',
  order = 'order_template.csv',
  asset = 'asset_template.csv',
  reorderRuleProduct = 'reorder_rule_product_template.csv',
}

export enum CsvUploadType {
  product = 'product',
  bin = 'bin',
  productMasterData = 'productMasterData',
  user = 'user',
  stockLocation = 'stockLocation',
  lot = 'lot',
  order = 'order',
  asset = 'asset',
  reorderRuleProduct = 'reorderRuleProduct',
}

enum CsvUploadConfigurationValidationError {
  name = 'Please enter a valid name',
}

export interface CsvUploadConfigurationMapping {
  field: string;
  customField: string;
}

export class CsvUploadConfiguration extends CompanyEntity {
  type!: CsvUploadType;
  name!: string;
  description?: string = undefined;
  headerMapping: CsvUploadConfigurationMapping[] = [];
  headerNames: string[] = [];

  mapping: Map<string, string> = new Map();

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId);
    Object.assign(this, cloneDeep(obj));
    this.mapping = new Map(this.headerMapping.map(value => [String(value['field']), value['customField']]));
  }

  forCreate(): CreateCsvUploadConfigurationInput {
    this.headerMapping = [...this.mapping.entries()].map(([key, value]) => {
      return { field: key, customField: value } as CsvUploadConfigurationMapping;
    });

    return {
      companyId: this.companyId,
      type: this.type,
      name: this.name,
      description: this.description,
      headerMapping: this.headerMapping,
      headerNames: this.headerNames,
    };
  }

  forUpdate(): UpdateCsvUploadConfigurationInput {
    this.headerMapping = [...this.mapping.entries()].map(([key, value]) => {
      return { field: key, customField: value } as CsvUploadConfigurationMapping;
    });

    return {
      id: this.id,
      companyId: this.companyId,
      version: this.version,
      name: this.name,
      type: this.type,
      description: this.description,
      headerNames: this.headerNames,
      headerMapping: this.headerMapping,
    };
  }

  forDelete(): DeleteCsvUploadConfigurationInput {
    return DeleteCsvUploadConfigurationInput.from(this, DeleteCsvUploadConfigurationInput);
  }

  validate(fields: (keyof CsvUploadConfiguration)[]) {
    return this.validateEntity(fields, (field: keyof CsvUploadConfiguration) => {
      if (field === 'name' && !this.name) return CsvUploadConfigurationValidationError.name;
      return null;
    });
  }

  withName(name: string) {
    this.name = name;
    return cloneDeep(this);
  }

  withType(type: CsvUploadType) {
    this.type = type;
    return cloneDeep(this);
  }

  withHeaderNames(headerNames: string[]) {
    this.headerNames = headerNames;
    return cloneDeep(this);
  }

  withMapping(field: string, customField: string) {
    this.mapping.set(field, customField);
    return cloneDeep(this);
  }

  removeMapping(field: string) {
    this.mapping.delete(field);
    return cloneDeep(this);
  }
}

export class CreateCsvUploadConfigurationInput {
  companyId!: string;
  type!: CsvUploadType;
  name!: string;
  description?: string = undefined;
  headerMapping: CsvUploadConfigurationMapping[] = [];
  headerNames: string[] = [];
}

export class UpdateCsvUploadConfigurationInput {
  id!: string;
  companyId!: string;
  version!: number;
  name!: string;
  type!: CsvUploadType;
  headerNames: string[] = [];
  description?: string = undefined;
  headerMapping: CsvUploadConfigurationMapping[] = [];
}

export class DeleteCsvUploadConfigurationInput extends forDelete(CsvUploadConfiguration) {}

function getCustomFieldEntityTypeByCsvHeaderPrefix(type: string) {
  if (type === 'productTransaction') {
    return t(TranslationKey.productTransaction).singular.label;
  } else if (type === 'order') {
    return t(TranslationKey.order).singular.label;
  } else if (type === 'pmd') {
    return t(TranslationKey.productMasterData).singular.label;
  }
}

export function ventoryFieldToText(field: string, customFields: Map<string, CustomField>) {
  if (field.includes('::')) return customFields.get(field.split('::')[1])?.name || '';
  return field;
}

export function fileNameToFileType(fileName: string) {
  if (fileName.endsWith('.csv')) {
    return CsvUploadFileType.csv;
  }

  if (fileName.endsWith('.xlsx')) {
    return CsvUploadFileType.xlsx;
  }

  throw new Error('Unsupported file type!');
}

export enum CsvStockLocationUploadHeaders {
  name = 'Stock Location Name',
  identifier = 'Stock Location Identifier',
  mobile = 'Mobile Stock Location',
  addressLineOne = 'Address Line 1',
  addressLineTwo = 'Address Line 2',
  postalCode = 'ZIP/Postal Code',
  city = 'City',
  state = 'State/Province/Region',
  country = 'Country Code',
}
